import type { PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxProps, StackProps } from '@chakra-ui/react';
import { Box, Divider, HStack, IconButton, SimpleGrid, SkeletonText, Text } from '@chakra-ui/react';

import { FeatherIcon } from '../icon';
import { Link } from '../Link/Link';

interface MenuSectionProps {
  title: string | ReactNode;
  headerRight?: ReactNode;
  withDivider?: boolean;
  containerProps?: BoxProps;
}

const MenuSection = ({
  title,
  withDivider = true,
  children,
  headerRight,
  containerProps,
}: PropsWithChildren<MenuSectionProps>) => (
  <Box
    as='section'
    borderColor='neutral100'
    borderWidth='1px'
    borderRadius='3xl'
    px={7}
    mt={8}
    boxShadow='0px 2px 6px rgba(0, 0, 0, 0.06)'
    alignSelf='stretch'
    {...containerProps}>
    <HStack justify='space-between' align='center'>
      {typeof title === 'string' ? (
        <Text variant='heading3' mb={5} mt={7} as='h2'>
          {title}
        </Text>
      ) : (
        title
      )}
      {headerRight}
    </HStack>
    {withDivider && <Divider />}
    {children}
  </Box>
);

interface MenuItemProps {
  icon: ReactNode;
  label: string | ReactNode;
  value?: ReactNode;
  href?: string;
  onClickEdit?: VoidFunction;
  containerProps?: StackProps;
  isLoading?: boolean;
  onClick?: VoidFunction;
  testId?: string;
}

const MenuItem = ({
  icon,
  label,
  value,
  containerProps,
  onClickEdit,
  onClick,
  href,
  isLoading,
  testId,
}: MenuItemProps) => {
  const { t } = useTranslation('shared');

  const renderAction = () => {
    if (onClickEdit) {
      return (
        <IconButton
          onClick={onClickEdit}
          variant='solid'
          aria-label={t('action.edit')}
          bg='neutral100'
          size='md'
          icon={<FeatherIcon name='edit-3' color='neutral500' />}
        />
      );
    }
    if (href) {
      return <FeatherIcon name='arrow-right' color='neutral500' />;
    }
    return null;
  };

  const menuItem = (
    <HStack
      justify='space-between'
      align='center'
      py={6}
      onClick={onClick}
      data-testid={testId}
      {...containerProps}>
      <SimpleGrid
        templateColumns={value ? { base: '1fr', md: '1fr 2fr 40px' } : '1fr 40px'}
        as='dl'
        flex={1}
        rowGap={2}
        columnGap={4}
        alignItems='center'>
        <HStack as='dt' gap={4}>
          {icon}
          {typeof label === 'string' ? <Text variant='heading5'>{label}</Text> : label}
        </HStack>
        {isLoading && (
          <SkeletonText noOfLines={1} as='dd' skeletonHeight={{ base: 4, md: 5 }} w='50%' />
        )}
        {!isLoading && Boolean(value) && (
          <Text variant='paragraph' as='dd' noOfLines={1}>
            {value}
          </Text>
        )}
        <HStack as='dd' display='flex' justifyContent='flex-end'>
          {renderAction()}
        </HStack>
      </SimpleGrid>
    </HStack>
  );

  return href ? (
    <Link to={href} display='block'>
      {menuItem}
    </Link>
  ) : (
    menuItem
  );
};

MenuSection.Item = MenuItem;

export default MenuSection;
